import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type { CuttysarkLiveView } from '@cortex/domain-product-innovation-lab-types';
import {
  BallotDetails,
  ControlPanel,
  ResultsView,
  SetupEditor,
} from '../components/features/cuttysark/index.js';
import { useCuttysarkBallots, useCuttysarkLive } from '../hooks/useCuttysark.js';
import { ApiError } from '../lib/api/_request.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの運営画面。候補者・参加者の設定、進行（紹介→発表→投票→締切→結果発表）、未投票者の確認、集計結果と投票明細の確認を運営だけが行う
 * @graph-connects useCuttysarkLive [calls] 進行状態のポーリング
 * @graph-connects useCuttysarkBallots [calls] 投票明細の取得
 * @graph-connects SetupEditor [calls] 設定フォーム
 * @graph-connects ControlPanel [calls] 進行パネル
 * @graph-connects ResultsView [calls] 集計結果の表示
 * @graph-connects BallotDetails [calls] 投票明細の表示
 */
export const Route = createFileRoute('/cuttysark/$sessionId/operate')({
  component: CuttysarkOperateRoute,
});

/**
 * ページ単位のガード（canAccessPage）は掛けない。PDG 以外の兼務者も、登録されている回は
 * 開けるようにするため、見られるかどうかは API（canViewCuttysarkSession）に任せ、
 * 403 のときは API のメッセージをそのまま出す。メニューは PDG・admin にだけ出る。
 * @graph-connects CuttysarkOperatePage [calls] ページ本体
 */
function CuttysarkOperateRoute(): ReactNode {
  return <CuttysarkOperatePage />;
}

/** @graph-connects none */
function CuttysarkOperatePage(): ReactNode {
  const { t } = useTranslation();
  const { sessionId } = Route.useParams();
  const { data: view, error, isLoading } = useCuttysarkLive(sessionId);

  if (isLoading) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.common.loading')}</p>
    );
  }
  if (view === undefined) {
    return (
      <p className="text-sm text-[var(--color-error)]">
        {error instanceof ApiError && error.status === 404
          ? t('cuttysark.common.notFound')
          : error instanceof ApiError && error.status === 403
            ? error.message
            : t('cuttysark.common.loadError')}
      </p>
    );
  }
  if (!view.viewer.isOperator) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.operate.forbidden')}</p>
    );
  }
  return <OperatorConsole sessionId={sessionId} view={view} />;
}

/** @graph-connects none */
interface OperatorConsoleProps {
  sessionId: string;
  view: CuttysarkLiveView;
}

/**
 * 運営の操作盤。設定フォームは他の運営に追い越されたら最新を読み直して作り直す。
 * @graph-connects useCuttysarkBallots [calls] 投票明細の取得
 */
function OperatorConsole({ sessionId, view }: OperatorConsoleProps): ReactNode {
  const { t } = useTranslation();
  const live = useCuttysarkLive(sessionId);
  const ballots = useCuttysarkBallots(sessionId, true);
  // 409 のたびに増やし、設定フォームを最新の内容で作り直す
  const [editorGeneration, setEditorGeneration] = useState(0);
  const [conflicted, setConflicted] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">{t(`cuttysark.stage.${view.stage}`)}</Badge>
            <Badge>{t(`cuttysark.phase.${view.phase}`)}</Badge>
            <span className="text-xs text-[var(--color-text-muted)]">{view.quarter}</span>
          </div>
          <h1 className="mt-1 text-lg font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.title')}: {view.title}
          </h1>
        </div>
        <div className="flex gap-2">
          {/* Meet で画面共有する演出画面。操作盤と並べて使えるよう別タブで開く */}
          <Link to="/cuttysark/$sessionId/stage" params={{ sessionId }} target="_blank">
            <Button type="button" size="sm" variant="primary">
              {t('cuttysark.operate.openStage')}
            </Button>
          </Link>
          <Link to="/cuttysark/$sessionId" params={{ sessionId }}>
            <Button type="button" size="sm" variant="secondary">
              {t('cuttysark.list.open')}
            </Button>
          </Link>
          <Link to="/cuttysark">
            <Button type="button" size="sm" variant="ghost">
              {t('cuttysark.common.back')}
            </Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.controlHeading')}
          </h2>
          <ControlPanel sessionId={sessionId} view={view} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.resultsHeading')}
          </h2>
          {view.results.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('cuttysark.operate.resultsNotYet')}
            </p>
          ) : (
            <ResultsView
              divisions={view.divisions}
              candidates={view.candidates}
              results={view.results}
              showTie
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.setupHeading')}
          </h2>
          {conflicted && (
            <p className="text-sm text-[var(--color-warning)]" role="alert">
              {t('cuttysark.operate.conflict')}
            </p>
          )}
          <SetupEditor
            key={editorGeneration}
            sessionId={sessionId}
            view={view}
            onConflict={() => {
              setConflicted(true);
              void live.refetch().then(() => {
                setEditorGeneration((g) => g + 1);
              });
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.ballotsHeading')}
          </h2>
          {ballots.isError ? (
            <p className="text-sm text-[var(--color-error)]">{ballots.error.message}</p>
          ) : (
            <BallotDetails ballots={ballots.data?.ballots ?? []} candidates={view.candidates} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
